import { useState, useRef, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { markActivityNow } from '../Auth/session';
import {
  Box,
  Button,
  Flex,
  Text,
  Input,
  Progress,
  Icon,
  Grid,
  GridItem,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Image,
  VStack,
  HStack,
  Badge,
  Divider,
  Spinner,
  Center,
} from '@chakra-ui/react';
import { CloseIcon, DownloadIcon, ExternalLinkIcon, InfoIcon, RepeatIcon } from '@chakra-ui/icons';
import { useToast } from '@chakra-ui/react';
import Chatbot from '../../components/ChatBot/Chatbot';
import { StorageProviderSelector, StorageProvider } from '../../components/upload/StorageProviderSelector';
import { CloudStorageURLInput } from '../../components/upload/CloudStorageURLInput';
import { StorageProviderInstructions } from '../../components/upload/StorageProviderInstructions';
import { FiHardDrive, FiFolder } from 'react-icons/fi';
import { BACKEND_API } from '../../constants/backendApiEndpoints';
import FeedbackModal from '../../components/common/FeedbackModal';
import { useFeedback } from '../../hooks/useFeedback';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Configure PDF worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// ID validation
const ID_RE = /^[A-Z0-9_-]{3,32}$/;
const normalizeId = (v: string) => v.replace(/\s+/g, '').toUpperCase();

// Detect file type from filename for genomics files
const getFileTypeFromName = (filename: string): string => {
  const lower = filename.toLowerCase();
  if (lower.endsWith('.fastq.gz') || lower.endsWith('.fq.gz')) return 'FASTQ.GZ';
  if (lower.endsWith('.fastq') || lower.endsWith('.fq')) return 'FASTQ';
  if (lower.endsWith('.bam')) return 'BAM';
  if (lower.endsWith('.cram')) return 'CRAM';
  if (lower.endsWith('.vcf.gz')) return 'VCF.GZ';
  if (lower.endsWith('.vcf')) return 'VCF';
  if (lower.endsWith('.sam')) return 'SAM';
  if (lower.endsWith('.bed')) return 'BED';
  if (lower.endsWith('.fa') || lower.endsWith('.fasta')) return 'FASTA';
  if (lower.endsWith('.fa.gz') || lower.endsWith('.fasta.gz')) return 'FASTA.GZ';
  return 'Unknown';
};

// Job status types
type JobStatus =
  | 'queued'
  | 'initialized'
  | 'validating'
  | 'running'
  | 'processing'
  | 'analyzing'
  | 'completed'
  | 'failed'
  | 'cancelled';

const progressMap: Record<JobStatus, number> = {
  queued: 5,
  initialized: 10,
  validating: 20,
  running: 40,
  processing: 60,
  analyzing: 80,
  completed: 100,
  failed: 100,
  cancelled: 100,
};

const statusMessages: Record<JobStatus, string> = {
  queued: 'Queued for processing...',
  initialized: 'Initializing pipeline...',
  validating: 'Validating inputs...',
  running: 'Running HaplotypeCaller...',
  processing: 'Processing variants...',
  analyzing: 'Preparing reports...',
  completed: 'Analysis complete!',
  failed: 'Analysis failed',
  cancelled: 'Analysis cancelled',
};

// MAF summary graph configurations
const GRAPH_CONFIGS = [
  { key: 'variantClassificationGraph' as const, label: 'Variant Classification', filename: 'variant_classification' },
  { key: 'variantTypeGraph' as const, label: 'Variant Type', filename: 'variant_type' },
  { key: 'snvClassGraph' as const, label: 'SNV Class', filename: 'snv_class' },
  { key: 'topGenesGraph' as const, label: 'Top 10 Genes', filename: 'top_genes' },
  { key: 'variantSummaryGraph' as const, label: 'Variant Summary', filename: 'variant_summary' },
  { key: 'mutationSpectrumGraph' as const, label: 'Variant Spectrum', filename: 'mutation_spectrum' },
];

// Fixed height for all graph cards
const GRAPH_CARD_HEIGHT = '220px';

// Graph card component - defined outside to prevent re-creation on parent renders
const GraphCard = ({ 
  graphUrl, 
  label, 
  filename,
  isComplete,
  onDownload,
  onImageClick,
}: { 
  graphUrl: string | undefined; 
  label: string; 
  filename: string;
  isComplete: boolean;
  onDownload: (url: string, filename: string) => void;
  onImageClick: (url: string, label: string) => void;
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  return (
    <Box
      border="1px solid"
      borderColor="surface.300"
      borderRadius="xl"
      overflow="hidden"
      bg="white"
      _hover={{ shadow: 'md', borderColor: 'primary.300' }}
      transition="all 0.2s"
      h="auto"
    >
      {/* Graph title */}
      <Flex bg="surface.100" px={4} py={2} borderBottom="1px solid" borderColor="surface.200" justifyContent="space-between" alignItems="center">
        <Text fontSize="sm" fontWeight="semibold" color="primary.600">
          {label}
        </Text>
        {graphUrl && imageLoaded && (
          <Icon
            as={DownloadIcon}
            boxSize={4}
            color="primary.400"
            cursor="pointer"
            _hover={{ color: 'primary.600' }}
            onClick={(e) => {
              e.stopPropagation();
              onDownload(graphUrl, `${filename}.jpg`);
            }}
            aria-label="Download graph"
          />
        )}
      </Flex>
      
      {/* Graph image or placeholder - fixed height container */}
      <Box h={GRAPH_CARD_HEIGHT} position="relative" bg="surface.50">
        {graphUrl && !imageError ? (
          <Box
            h="100%"
            cursor="pointer"
            onClick={() => onImageClick(graphUrl, label)}
            title="Click to enlarge"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            {!imageLoaded && (
              <Text color="muted.400" fontSize="sm">Loading...</Text>
            )}
            <img
              src={graphUrl}
              alt={label}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
                display: imageLoaded ? 'block' : 'none',
              }}
              onLoad={() => setImageLoaded(true)}
              onError={() => setImageError(true)}
            />
          </Box>
        ) : imageError ? (
          <Box
            h="100%"
            display="flex"
            alignItems="center"
            justifyContent="center"
            bg="red.50"
          >
            <Text color="red.400" fontSize="sm">Failed to load graph</Text>
          </Box>
        ) : (
          <Box
            h="100%"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Text color="muted.400" fontSize="sm">
              {isComplete ? 'Graph not available' : 'Pending...'}
            </Text>
          </Box>
        )}
      </Box>
    </Box>
  );
};

const HaplotypeCaller = () => {
  // Refs
  const read1InputRef = useRef<HTMLInputElement>(null);
  const read2InputRef = useRef<HTMLInputElement>(null);
  const patientInputRef = useRef<HTMLInputElement>(null);
  const sampleInputRef = useRef<HTMLInputElement>(null);

  // Step 1: ID inputs
  const [localPatientId, setLocalPatientId] = useState('');
  const [localSampleId, setLocalSampleId] = useState('');

  // Step 2: File uploads
  const [, setRead1File] = useState<File | null>(null);
  const [read1FileName, setRead1FileName] = useState<string | null>(null);
  const [read1FileId, setRead1FileId] = useState<number | null>(null);
  const [read1StorageProvider, setRead1StorageProvider] = useState<StorageProvider>('local');
  const [read1CloudURL, setRead1CloudURL] = useState('');

  const [, setRead2File] = useState<File | null>(null);
  const [read2FileName, setRead2FileName] = useState<string | null>(null);
  const [_read2FileId, setRead2FileId] = useState<number | null>(null);
  const [read2StorageProvider, setRead2StorageProvider] = useState<StorageProvider>('local');
  const [read2CloudURL, setRead2CloudURL] = useState('');

  // Shared upload UUID for paired reads (Read 1 + Read 2 in same folder)
  // Using useRef instead of useState to avoid race condition when uploading both files simultaneously
  const sharedUploadUuidRef = useRef<string | null>(null);

  // Generate UUID synchronously to prevent race conditions
  const getOrCreateUploadUuid = (): string => {
    if (!sharedUploadUuidRef.current) {
      sharedUploadUuidRef.current = crypto.randomUUID();
    }
    return sharedUploadUuidRef.current;
  };

  // Job state
  const [jobId, setJobId] = useState<number | null>(null);
  const [progress, setProgress] = useState(0);
  const [statusDetail, setStatusDetail] = useState<string>('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  
  // Separate upload state for each read
  const [isUploadingRead1, setIsUploadingRead1] = useState(false);
  const [uploadProgressRead1, setUploadProgressRead1] = useState(0);
  const [isUploadingRead2, setIsUploadingRead2] = useState(false);
  const [uploadProgressRead2, setUploadProgressRead2] = useState(0);
  // Track if upload is from cloud (indeterminate progress)
  const [isCloudTransferRead1, setIsCloudTransferRead1] = useState(false);
  const [isCloudTransferRead2, setIsCloudTransferRead2] = useState(false);

  // Results
  const [resultFiles, setResultFiles] = useState<{
    vcf?: string;
    annotatedVcf?: string;
    maf?: string;
    bam?: string;
    pdf?: string;
    // MAF summary graphs
    variantClassificationGraph?: string;
    variantTypeGraph?: string;
    snvClassGraph?: string;
    topGenesGraph?: string;
    variantSummaryGraph?: string;
    mutationSpectrumGraph?: string;
  }>({});

  // Modal state for enlarged graph view
  const { isOpen: isGraphModalOpen, onOpen: onGraphModalOpen, onClose: onGraphModalClose } = useDisclosure();
  const [selectedGraph, setSelectedGraph] = useState<{ url: string; label: string } | null>(null);

  // PDF viewer state
  const [numPages, setNumPages] = useState<number | null>(null);

  function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
    setNumPages(numPages);
  }

  const handleGraphClick = useCallback((url: string, label: string) => {
    setSelectedGraph({ url, label });
    onGraphModalOpen();
  }, [onGraphModalOpen]);

  // Previous files state
  interface PreviousFile {
    key: string;
    filename: string;
    relative_path: string;
    pipeline_folder: string;
    file_type: 'fastq';
    size: number;
    last_modified: string | null;
  }
  const [previousFiles, setPreviousFiles] = useState<PreviousFile[]>([]);
  const [isLoadingPreviousFiles, setIsLoadingPreviousFiles] = useState(false);
  const [selectingForRead, setSelectingForRead] = useState<1 | 2 | null>(null);
  const { isOpen: isPreviousFilesOpen, onOpen: onOpenPreviousFiles, onClose: onClosePreviousFiles } = useDisclosure();

  // Feedback modal
  const { showFeedbackModal, setShowFeedbackModal } = useFeedback({
    jobId,
    pipelineType: 'Haplotype Caller',
    jobStatus: isComplete ? 'completed' : statusDetail,
    delayMs: 5000,
  });

  // Computed validation
  const normalizedPatientId = normalizeId(localPatientId);
  const normalizedSampleId = normalizeId(localSampleId);
  const pidOk = !normalizedPatientId || ID_RE.test(normalizedPatientId);
  const sidOk = !!normalizedSampleId && ID_RE.test(normalizedSampleId);
  const idsValid = pidOk && sidOk;

  // File validation - need both paired-end reads
  const hasRead1 = !!read1FileId || read1CloudURL.startsWith('s3://');
  const hasRead2 = !!_read2FileId || read2CloudURL.startsWith('s3://');
  const hasBothReads = hasRead1 && hasRead2;

  // S3 upload constants
  const PART_SIZE = 50 * 1024 * 1024; // 50MB parts for better throughput
  const CONCURRENT_UPLOADS = 4; // Upload 4 parts in parallel
  const MULTIPART_THRESHOLD = 100 * 1024 * 1024; // Use multipart for files > 100MB

  const toast = useToast();

  const showToast = useCallback((
    title: string,
    status: 'info' | 'warning' | 'success' | 'error'
  ) =>
    toast({ title, status, duration: 5000, isClosable: true, position: 'top' }), [toast]);

  // Caret-preserving input handler
  const handleTextChange =
    (ref: React.RefObject<HTMLInputElement>, setValue: (v: string) => void) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value, selectionStart } = e.target;
      setValue(value);
      requestAnimationFrame(() => {
        if (ref.current) {
          ref.current.focus();
          const pos = selectionStart ?? value.length;
          try {
            ref.current.setSelectionRange(pos, pos);
          } catch {}
        }
      });
    };



  // Fetch job results when completed - same pattern as PGX
  const fetchJobResults = useCallback(async (id: number, retries: number = 3): Promise<boolean> => {
    const token = localStorage.getItem('access_token');
    try {
      const res = await fetch(BACKEND_API.HAPLOTYPE_CALLER.RESULTS(id), {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({ detail: 'Unknown error' }));
        console.error('Failed to fetch results:', errorData);
        if (retries > 0) {
          await new Promise(resolve => setTimeout(resolve, 2000));
          return fetchJobResults(id, retries - 1);
        }
        showToast(`Failed to fetch results: ${errorData.detail || 'Unknown error'}`, 'error');
        return false;
      }
      
      const data = await res.json();
      const files = data.files || {};
      
      // Map backend keys to frontend keys
      // Backend sends: vcf, annotated_vcf, maf, bam, clinical_report_pdf, and graph files
      setResultFiles({
        vcf: files.vcf || undefined,                    // Plain VCF from Step 1
        annotatedVcf: files.annotated_vcf || undefined, // Annotated VCF from Step 2
        maf: files.maf || undefined,
        bam: files.bam || undefined,
        pdf: files.clinical_report_pdf || undefined,
        // MAF summary graphs
        variantClassificationGraph: files.variant_classification_graph || undefined,
        variantTypeGraph: files.variant_type_graph || undefined,
        snvClassGraph: files.snv_class_graph || undefined,
        topGenesGraph: files.top_genes_graph || undefined,
        variantSummaryGraph: files.variant_summary_graph || undefined,
        mutationSpectrumGraph: files.mutation_spectrum_graph || undefined,
      });
      return true;
    } catch (err) {
      console.error('Failed to fetch results:', err);
      if (retries > 0) {
        await new Promise(resolve => setTimeout(resolve, 2000));
        return fetchJobResults(id, retries - 1);
      }
      showToast('Network error while fetching results. Please try again.', 'error');
      return false;
    }
  }, [showToast]);

  // URL search params for direct navigation
  const [searchParams] = useSearchParams();

  // Handle direct navigation with jobId param (from dashboard)
  useEffect(() => {
    const urlJobId = searchParams.get('jobId');
    const urlView = searchParams.get('view');
    if (urlJobId && urlView === 'completed') {
      const parsedJobId = parseInt(urlJobId, 10);
      if (!isNaN(parsedJobId)) {
        setJobId(parsedJobId);
        setIsComplete(true);
        setProgress(100);
        fetchJobResults(parsedJobId);
      }
    }
  }, [searchParams, fetchJobResults]);

  // Real job status polling
  useEffect(() => {
    if (!jobId) return;
    // Don't poll if already complete
    if (isComplete) return;
    
    const pollStatus = async () => {
      const token = localStorage.getItem('access_token');
      try {
        const res = await fetch(BACKEND_API.HAPLOTYPE_CALLER.STATUS(jobId), {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          console.error('Status poll failed:', res.status);
          return;
        }
        const data = await res.json();
        const status = data.status as JobStatus;
        
        setStatusDetail(statusMessages[status] || status);
        setProgress(data.progress || progressMap[status] || 0);
        
        // Check for terminal states
        if (status === 'completed') {
          setIsComplete(true);
          setIsAnalyzing(false);
          fetchJobResults(jobId);
          return true; // Signal to stop polling
        } else if (status === 'failed' || status === 'cancelled') {
          setIsAnalyzing(false);
          showToast(`Analysis ${status}`, status === 'failed' ? 'error' : 'warning');
          return true; // Signal to stop polling
        }
        return false;
      } catch (err) {
        console.error('Status poll error:', err);
        return false;
      }
    };
    
    // Initial poll
    pollStatus();
    
    // Set up polling interval (every 10 seconds)
    const pollInterval = setInterval(async () => {
      const shouldStop = await pollStatus();
      if (shouldStop) {
        clearInterval(pollInterval);
      }
    }, 10000);

    return () => clearInterval(pollInterval);
  }, [jobId, isComplete, fetchJobResults]);

  // Get presigned URL for S3 upload
  const getS3PresignedUrl = async (filename: string, contentType: string = 'application/octet-stream', existingUuid?: string | null, readNumber?: number) => {
    const token = localStorage.getItem('access_token');
    const params = new URLSearchParams({
      filename,
      content_type: contentType,
    });
    // Pass existing UUID to reuse folder for paired reads
    if (existingUuid) {
      params.append('upload_uuid', existingUuid);
    }
    // Pass read_number for subfolder structure
    if (readNumber) {
      params.append('read_number', readNumber.toString());
    }
    const res = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/s3/uploads/presign?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Failed to get presigned URL');
    return await res.json(); // { upload_url, key, upload_uuid, method, headers }
  };

  // Initiate S3 multipart upload
  const initiateS3MultipartUpload = async (filename: string, contentType: string = 'application/octet-stream', existingUuid?: string | null, readNumber?: number) => {
    const token = localStorage.getItem('access_token');
    const res = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/s3/uploads/multipart/initiate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ 
        filename, 
        content_type: contentType,
        upload_uuid: existingUuid || undefined,  // Pass existing UUID for paired reads
        read_number: readNumber || undefined,  // Pass read number for subfolder
      }),
    });
    if (!res.ok) throw new Error('Failed to initiate multipart upload');
    return await res.json(); // { upload_id, key, upload_uuid }
  };

  // Get presigned URL for a specific part
  const getS3PartPresignedUrl = async (key: string, uploadId: string, partNumber: number) => {
    const token = localStorage.getItem('access_token');
    const params = new URLSearchParams({
      key,
      upload_id: uploadId,
      part_number: partNumber.toString(),
    });
    const res = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/s3/uploads/multipart/presign-part?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(`Failed to get presigned URL for part ${partNumber}`);
    return await res.json(); // { upload_url, part_number, method }
  };

  // Complete S3 multipart upload
  const completeS3MultipartUpload = async (uploadId: string, key: string, parts: { PartNumber: number; ETag: string }[], fileType: string) => {
    const token = localStorage.getItem('access_token');
    const res = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/s3/uploads/multipart/complete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ upload_id: uploadId, key, parts, file_type: fileType }),
    });
    if (!res.ok) throw new Error('Failed to complete multipart upload');
    return await res.json(); // { message, file_id, upload_uuid, size, url }
  };

  // Confirm S3 upload (for single PUT uploads)
  const confirmS3Upload = async (key: string, filename: string, fileType: string) => {
    const token = localStorage.getItem('access_token');
    const res = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/s3/uploads/confirm-upload`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ key, filename, file_type: fileType }),
    });
    if (!res.ok) throw new Error('Failed to confirm upload');
    return await res.json(); // { message, file_id, upload_uuid, size }
  };

  // Upload file to S3 using presigned URL (single PUT for small files)
  const uploadToS3Single = async (file: File, presignedUrl: string, contentType: string, setProgressFn: (p: number) => void) => {
    return new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', presignedUrl, true);
      xhr.setRequestHeader('Content-Type', contentType);
      
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          setProgressFn(Math.round((e.loaded / e.total) * 100));
        }
      };
      
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`));
        }
      };
      
      xhr.onerror = () => reject(new Error('Network error during upload'));
      xhr.send(file);
    });
  };

  // Upload file to S3 using multipart with parallel batch uploads (for large files)
  const uploadToS3Multipart = async (file: File, uploadId: string, key: string, setProgressFn: (p: number) => void) => {
    const numParts = Math.ceil(file.size / PART_SIZE);
    const parts: { PartNumber: number; ETag: string }[] = new Array(numParts);
    let uploadedBytes = 0;

    // Upload a single part
    const uploadPart = async (partNumber: number): Promise<void> => {
      const start = (partNumber - 1) * PART_SIZE;
      const end = Math.min(start + PART_SIZE, file.size);
      const chunk = file.slice(start, end);

      // Get presigned URL for this part
      const { upload_url } = await getS3PartPresignedUrl(key, uploadId, partNumber);

      // Upload the part
      const etag = await new Promise<string>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', upload_url, true);
        
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            const etag = xhr.getResponseHeader('ETag');
            if (etag) {
              resolve(etag.replace(/"/g, ''));
            } else {
              reject(new Error('No ETag in response'));
            }
          } else {
            reject(new Error(`Part upload failed: ${xhr.status}`));
          }
        };
        
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.send(chunk);
      });

      parts[partNumber - 1] = { PartNumber: partNumber, ETag: etag };
      uploadedBytes += (end - start);
      setProgressFn(Math.round((uploadedBytes / file.size) * 100));
      markActivityNow(); // Keep session alive during long uploads
    };

    // Upload parts in parallel batches
    for (let i = 0; i < numParts; i += CONCURRENT_UPLOADS) {
      const batch = [];
      for (let j = i; j < Math.min(i + CONCURRENT_UPLOADS, numParts); j++) {
        batch.push(uploadPart(j + 1));
      }
      await Promise.all(batch);
    }

    return parts;
  };

  // S3 file upload for Read 1 or Read 2
  const handleLocalFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
    readNumber: 1 | 2
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!idsValid) {
      showToast('Please enter valid Sample ID before uploading.', 'warning');
      return;
    }

    const setFile = readNumber === 1 ? setRead1File : setRead2File;
    const setFileName = readNumber === 1 ? setRead1FileName : setRead2FileName;
    const setFileId = readNumber === 1 ? setRead1FileId : setRead2FileId;
    const setIsUploading = readNumber === 1 ? setIsUploadingRead1 : setIsUploadingRead2;
    const setUploadProgress = readNumber === 1 ? setUploadProgressRead1 : setUploadProgressRead2;
    const setIsCloudTransfer = readNumber === 1 ? setIsCloudTransferRead1 : setIsCloudTransferRead2;

    setFile(file);
    setIsUploading(true);
    setIsCloudTransfer(false); // Local upload has real progress
    setUploadProgress(0);

    try {
      const contentType = 'application/octet-stream';
      const fileType = getFileTypeFromName(file.name);
      // Get or create shared UUID synchronously to prevent race condition
      const existingUuid = getOrCreateUploadUuid();
      
      if (file.size > MULTIPART_THRESHOLD) {
        // Large file: use multipart upload
        const { upload_id, key } = await initiateS3MultipartUpload(file.name, contentType, existingUuid, readNumber);
        const parts = await uploadToS3Multipart(file, upload_id, key, setUploadProgress);
        const result = await completeS3MultipartUpload(upload_id, key, parts, fileType);
        
        setFileName(file.name);
        setFileId(result.file_id);
        showToast(`Read ${readNumber} uploaded successfully!`, 'success');
      } else {
        // Small file: use single PUT
        const { upload_url, key } = await getS3PresignedUrl(file.name, contentType, existingUuid, readNumber);
        await uploadToS3Single(file, upload_url, contentType, setUploadProgress);
        const result = await confirmS3Upload(key, file.name, fileType);
        
        setFileName(file.name);
        setFileId(result.file_id);
        showToast(`Read ${readNumber} uploaded successfully!`, 'success');
      }
    } catch (err) {
      console.error('Upload failed:', err);
      showToast(`Upload failed: ${err instanceof Error ? err.message : 'Unknown error'}`, 'error');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  // Cloud URL submission - transfers file from external URL to our S3
  const handleCloudURLSubmit = async (readNumber: 1 | 2) => {
    const cloudURL = readNumber === 1 ? read1CloudURL : read2CloudURL;
    const setFileName = readNumber === 1 ? setRead1FileName : setRead2FileName;
    const setFileId = readNumber === 1 ? setRead1FileId : setRead2FileId;
    const setIsUploading = readNumber === 1 ? setIsUploadingRead1 : setIsUploadingRead2;
    const setUploadProgress = readNumber === 1 ? setUploadProgressRead1 : setUploadProgressRead2;
    const setIsCloudTransfer = readNumber === 1 ? setIsCloudTransferRead1 : setIsCloudTransferRead2;

    if (!cloudURL.trim() || !idsValid) {
      showToast('Please enter a valid URL and Sample ID', 'warning');
      return;
    }

    // Extract filename from URL
    const urlFileName = cloudURL.split('/').pop()?.split('?')[0] || 'cloud_file.fastq.gz';
    const fileType = getFileTypeFromName(urlFileName);
    
    // Get or create shared UUID
    const existingUuid = getOrCreateUploadUuid();

    setIsUploading(true);
    setIsCloudTransfer(false); // Now using real progress
    setUploadProgress(0);

    try {
      const token = localStorage.getItem('access_token');
      
      // Start async transfer
      const res = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/s3/uploads/transfer-from-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          source_url: cloudURL,
          filename: urlFileName,
          upload_uuid: existingUuid,
          read_number: readNumber,
          file_type: fileType,
        }),
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({ detail: 'Transfer failed' }));
        throw new Error(errorData.detail || 'Transfer failed');
      }

      const { transfer_id } = await res.json();
      
      // Poll for status
      let completed = false;
      while (!completed) {
        await new Promise(resolve => setTimeout(resolve, 1500)); // Poll every 1.5s
        
        const statusRes = await fetch(
          `${import.meta.env.VITE_API_URL}/api/v1/s3/uploads/transfer-from-url/${transfer_id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (!statusRes.ok) {
          throw new Error('Failed to get transfer status');
        }
        
        const status = await statusRes.json();
        setUploadProgress(status.progress || 0);
        
        if (status.status === 'completed') {
          completed = true;
          setFileName(urlFileName);
          setFileId(status.file_id);
          showToast(`Read ${readNumber} transferred successfully!`, 'success');
        } else if (status.status === 'failed') {
          throw new Error(status.error || 'Transfer failed');
        }
        // else continue polling (pending, downloading, uploading)
      }
      
    } catch (err) {
      console.error('Cloud transfer failed:', err);
      const message = err instanceof Error ? err.message : 'Unknown error';
      showToast(`Transfer failed: ${message}`, 'error');
    } finally {
      setIsUploading(false);
      setIsCloudTransfer(false);
      setUploadProgress(0);
    }
  };

  // Clear file upload
  const clearFileUpload = (readNumber: 1 | 2) => {
    if (readNumber === 1) {
      setRead1File(null);
      setRead1FileName(null);
      setRead1FileId(null);
      setRead1CloudURL('');
    } else {
      setRead2File(null);
      setRead2FileName(null);
      setRead2FileId(null);
      setRead2CloudURL('');
    }
  };

  // Fetch previous files from S3
  const fetchPreviousFiles = async () => {
    setIsLoadingPreviousFiles(true);
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch(BACKEND_API.HAPLOTYPE_CALLER.PREVIOUS_FILES, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        throw new Error('Failed to fetch previous files');
      }

      const data = await res.json();
      setPreviousFiles(data.files || []);
    } catch (err) {
      console.error('Error fetching previous files:', err);
      showToast('Failed to load previous files', 'error');
    } finally {
      setIsLoadingPreviousFiles(false);
    }
  };

  // Open previous files modal
  const openSelectPrevious = (readNumber: 1 | 2) => {
    setSelectingForRead(readNumber);
    fetchPreviousFiles();
    onOpenPreviousFiles();
  };

  // Select a previous file
  const selectPreviousFile = (file: PreviousFile) => {
    if (!selectingForRead) return;

    if (selectingForRead === 1) {
      setRead1FileName(file.filename);
      // Store the S3 key as the cloud URL for use in job submission
      setRead1CloudURL(`s3://seq4life-storage/${file.key}`);
      setRead1StorageProvider('previous');
      setRead1FileId(null); // No file upload record, using existing S3 path
    } else {
      setRead2FileName(file.filename);
      setRead2CloudURL(`s3://seq4life-storage/${file.key}`);
      setRead2StorageProvider('previous');
      setRead2FileId(null);
    }

    showToast(`Selected: ${file.filename}`, 'success');
    onClosePreviousFiles();
    setSelectingForRead(null);
  };

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  // Start analysis - calls backend API to trigger HealthOmics pipeline
  const handleAnalysis = async () => {
    // Check if we have both files - either uploaded or from previous files
    const hasRead1FromUpload = !!read1FileId;
    const hasRead1FromPrevious = read1CloudURL.startsWith('s3://');
    const hasRead2FromUpload = !!_read2FileId;
    const hasRead2FromPrevious = read2CloudURL.startsWith('s3://');
    
    if (!hasRead1FromUpload && !hasRead1FromPrevious) {
      showToast('Please upload Read 1 FASTQ file.', 'warning');
      return;
    }
    if (!hasRead2FromUpload && !hasRead2FromPrevious) {
      showToast('Please upload Read 2 FASTQ file. Paired-end reads are required.', 'warning');
      return;
    }
    if (!sidOk) {
      showToast('Please enter a valid Sample ID.', 'warning');
      return;
    }
    
    // For previous files, we don't need upload_uuid
    const isUsingPreviousFiles = hasRead1FromPrevious && !hasRead1FromUpload;
    
    if (!isUsingPreviousFiles && !sharedUploadUuidRef.current) {
      showToast('Upload not completed. Please re-upload the file.', 'error');
      return;
    }

    setIsAnalyzing(true);
    setProgress(5);
    setStatusDetail('Starting analysis...');

    try {
      const token = localStorage.getItem('access_token');
      const formData = new FormData();
      
      if (isUsingPreviousFiles) {
        // Using previous files - pass S3 paths directly
        formData.append('read1_s3_path', read1CloudURL);
        if (read2CloudURL.startsWith('s3://')) {
          formData.append('read2_s3_path', read2CloudURL);
        }
      } else {
        // Normal upload flow
        formData.append('upload_uuid', sharedUploadUuidRef.current!);
      }
      
      formData.append('reference_genome', 'NCBI_GRCh38'); // Default reference genome
      formData.append('sample_id', normalizedSampleId);
      if (normalizedPatientId) {
        formData.append('patient_id', normalizedPatientId);
      }

      const res = await fetch(BACKEND_API.HAPLOTYPE_CALLER.RUN, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.detail || `Request failed: ${res.status}`);
      }

      const data = await res.json();
      setJobId(data.job_id);
      showToast('Analysis started successfully!', 'success');
    } catch (err) {
      console.error('Failed to start analysis:', err);
      showToast(`Failed to start analysis: ${err instanceof Error ? err.message : 'Unknown error'}`, 'error');
      setIsAnalyzing(false);
      setProgress(0);
      setStatusDetail('');
    }
  };

  // Reset only job state (keeps uploaded files for re-run)
  const resetJobState = () => {
    setProgress(0);
    setIsComplete(false);
    setJobId(null);
    setResultFiles({});
    setStatusDetail('');
    setIsAnalyzing(false);
  };

  // Reset UI (full reset including files)
  const resetToStart = () => {
    setRead1File(null);
    setRead1FileName(null);
    setRead1FileId(null);
    setRead1CloudURL('');
    setRead2File(null);
    setRead2FileName(null);
    setRead2FileId(null);
    setRead2CloudURL('');
    setProgress(0);
    setIsComplete(false);
    setJobId(null);
    setResultFiles({});
    setStatusDetail('');
    setLocalPatientId('');
    setLocalSampleId('');
    setIsAnalyzing(false);
    sharedUploadUuidRef.current = null;  // Reset shared UUID for new upload session
  };

  // Cancel job - calls backend API to cancel HealthOmics run
  const handleCancelJob = async () => {
    if (!jobId) return;
    setIsCancelling(true);
    
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch(BACKEND_API.HAPLOTYPE_CALLER.CANCEL(jobId), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.detail || `Cancel failed: ${res.status}`);
      }

      showToast('Job cancelled. You can re-run the analysis.', 'success');
      resetJobState();  // Only reset job state, keep files
    } catch (err) {
      console.error('Failed to cancel job:', err);
      showToast(`Failed to cancel: ${err instanceof Error ? err.message : 'Unknown error'}`, 'error');
    } finally {
      setIsCancelling(false);
    }
  };

  // Download handler - same approach as PGX (direct link, no fetch)
  const handleDownload = (url: string | undefined, filename: string) => {
    if (!url) {
      showToast(`${filename} is not available yet.`, 'warning');
      return;
    }
    // Direct link approach - presigned URLs don't need auth headers
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // PDF download handler - uses blob to force download instead of opening in browser
  const handlePdfDownload = async (url: string | undefined, filename: string) => {
    if (!url) {
      showToast(`${filename} is not available yet.`, 'warning');
      return;
    }
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('PDF download failed:', err);
      showToast(`Failed to download ${filename}`, 'error');
    }
  };

  // ==================== UI COMPONENTS ====================

  // ID inputs row
  const IdInputsRow = () => (
    <Flex mt={8} direction="column" maxW="400px" mx="auto" gap={6}>
      <Box>
        <Text mb={1} fontWeight="medium" fontSize="lg" textAlign="left">
          Sample ID*
        </Text>
        <Input
          placeholder="Enter the sample name or ID"
          value={localSampleId}
          onChange={handleTextChange(sampleInputRef, setLocalSampleId)}
          ref={sampleInputRef}
          maxLength={32}
          borderColor={localSampleId && !sidOk ? 'red.400' : 'surface.300'}
          focusBorderColor="primary.500"
          autoComplete="off"
          spellCheck={false}
          size="lg"
          bg="white"
          borderRadius="md"
          _hover={{ bg: 'surface.100' }}
          _focus={{ bg: 'white', borderColor: 'primary.500', boxShadow: 'none' }}
        />
        {localSampleId && !sidOk && (
          <Text mt={1} fontSize="sm" color="red.500" textAlign="left">
            3–32 chars: A–Z, 0–9, _ or -
          </Text>
        )}
      </Box>
      <Box>
        <Text mb={1} fontWeight="medium" fontSize="lg" textAlign="left">
          Patient ID
        </Text>
        <Input
          placeholder="Enter the patient ID"
          value={localPatientId}
          onChange={handleTextChange(patientInputRef, setLocalPatientId)}
          ref={patientInputRef}
          maxLength={32}
          borderColor={localPatientId && !pidOk ? 'red.400' : 'surface.300'}
          focusBorderColor="primary.500"
          autoComplete="off"
          spellCheck={false}
          size="lg"
          bg="white"
          borderRadius="md"
          _hover={{ bg: 'surface.100' }}
          _focus={{ bg: 'white', borderColor: 'primary.500', boxShadow: 'none' }}
        />
        {localPatientId && !pidOk && (
          <Text mt={1} fontSize="sm" color="red.500" textAlign="left">
            3–32 chars: A–Z, 0–9, _ or -
          </Text>
        )}
      </Box>
    </Flex>
  );

  // File upload section
  const FileUploadSection = ({
    readNumber,
    isOptional = false,
  }: {
    readNumber: 1 | 2;
    isOptional?: boolean;
  }) => {
    const fileName = readNumber === 1 ? read1FileName : read2FileName;
    const fileId = readNumber === 1 ? read1FileId : _read2FileId;
    const storageProvider = readNumber === 1 ? read1StorageProvider : read2StorageProvider;
    const setStorageProvider = readNumber === 1 ? setRead1StorageProvider : setRead2StorageProvider;
    const cloudURL = readNumber === 1 ? read1CloudURL : read2CloudURL;
    const setCloudURL = readNumber === 1 ? setRead1CloudURL : setRead2CloudURL;
    const inputRef = readNumber === 1 ? read1InputRef : read2InputRef;
    const isUploading = readNumber === 1 ? isUploadingRead1 : isUploadingRead2;
    const uploadProgress = readNumber === 1 ? uploadProgressRead1 : uploadProgressRead2;
    const isCloudTransfer = readNumber === 1 ? isCloudTransferRead1 : isCloudTransferRead2;
    
    // Determine if this is a reused file from previous uploads
    const isFromPrevious = cloudURL.startsWith('s3://') && !fileId;

    return (
      <Box
        mt={0}
        p={6}
        border="2px solid rgba(0,0,0,0.06)"
        borderRadius="2xl"
        bg="white"
        w="100%"
      >
        <Flex justify="space-between" align="center" mb={4}>
          <Text fontSize="lg" fontWeight="semibold" color="primary.500">
            Read {readNumber} {isOptional && '(optional)'}
          </Text>
          {fileName && (
            <Button
              size="sm"
              variant="ghost"
              colorScheme="red"
              leftIcon={<CloseIcon boxSize={3} />}
              onClick={() => clearFileUpload(readNumber)}
            >
              Remove
            </Button>
          )}
        </Flex>

        {fileName ? (
          <Flex
            align="center"
            justify="space-between"
            p={4}
            bg="surface.100"
            borderRadius="xl"
          >
            <Text fontWeight="medium" color="primary.500" isTruncated maxW="400px">
              {fileName}
            </Text>
            <Badge colorScheme={isFromPrevious ? 'blue' : 'green'} fontSize="sm" px={2} py={1}>
              {isFromPrevious ? 'From Previous' : 'Uploaded'}
            </Badge>
          </Flex>
        ) : (
          <>
            {/* Storage provider selector */}
            <StorageProviderSelector
              selectedProvider={storageProvider}
              onChange={(provider) => {
                // Check if Sample ID is valid before allowing file selection
                if (!idsValid && provider !== 'local') {
                  showToast('Please enter a valid Sample ID first (3-32 characters: A-Z, 0-9, _ or -)', 'warning');
                  return;
                }
                setStorageProvider(provider);
                // If previous files selected, open modal directly
                if (provider === 'previous') {
                  openSelectPrevious(readNumber);
                }
              }}
              showPrevious={true}
            />

            {/* Local upload */}
            {storageProvider === 'local' && (
              <Flex mt={4} justify="center" align="center" gap={4}>
                <Input
                  type="file"
                  ref={inputRef}
                  display="none"
                  accept=".fastq,.fq,.fastq.gz,.fq.gz,.gz,application/gzip,application/x-gzip"
                  onChange={(e) => handleLocalFileUpload(e, readNumber)}
                  disabled={!idsValid}
                />
                <Button
                  leftIcon={<Icon as={FiHardDrive} />}
                  bg="primary.500"
                  color="white"
                  fontWeight="bold"
                  px={8}
                  py={6}
                  borderRadius="full"
                  onClick={() => inputRef.current?.click()}
                  isDisabled={!idsValid || isUploading}
                  isLoading={isUploading}
                  loadingText="Uploading..."
                  _hover={{ bg: 'secondary.500', transform: 'scale(1.03)' }}
                  _active={{ bg: 'primary.500', transform: 'scale(0.99)' }}
                  transition="all 0.18s ease-in-out"
                >
                  Upload from Local
                </Button>
              </Flex>
            )}

            {/* Cloud storage */}
            {storageProvider !== 'local' && (
              <>
                <CloudStorageURLInput
                  provider={storageProvider}
                  value={cloudURL}
                  onChange={setCloudURL}
                  onSubmit={() => handleCloudURLSubmit(readNumber)}
                  isDisabled={!idsValid}
                />
                <StorageProviderInstructions provider={storageProvider} />
              </>
            )}
          </>
        )}

        {/* Upload progress */}
        {isUploading && (
          <Box mt={4}>
            <Progress
              value={isCloudTransfer ? undefined : uploadProgress}
              isIndeterminate={isCloudTransfer}
              colorScheme="purple"
              size="lg"
              hasStripe
              isAnimated
              borderRadius="full"
            />
            <Text mt={2} fontSize="sm" color="muted.400" textAlign="center">
              {isCloudTransfer ? 'Transferring from cloud storage...' : `${uploadProgress}% uploaded`}
            </Text>
          </Box>
        )}
      </Box>
    );
  };



  // ==================== MAIN RENDER ====================

  // Determine current step
  const showUploadForm = !isAnalyzing && !isComplete;
  const showProgress = isAnalyzing && !isComplete;
  const showResults = isComplete;

  return (
    <Box p={8} textAlign="center" position="relative">
      {/* Page Title */}
      <Text
        fontSize={{ base: '2xl', md: '3xl' }}
        fontWeight="bold"
        color="primary.500"
        mb={4}
      >
        Germline Mutation Analysis – Haplotype Caller
      </Text>

      {/* Description Box */}
      <Text
        fontSize={{ base: 'md', md: 'lg' }}
        color="primary.500"
        maxW={{ base: '900px', md: '1000px' }}
        mx="auto"
        textAlign={{ base: 'left', md: 'justify' }}
        mb={8}
        lineHeight={1.7}
      >
        Precisely detect germline mutations using the traditional GATK HaplotypeCaller. 
        Our pipeline delivers high-confidence variant calls from BAM files aligned to a 
        standardized reference genome. Variants are scored with ACMG criteria and refined 
        using population scale data to support clinical accuracy, including rare disease 
        research and hereditary cancer screening.
      </Text>

      {/* STEP 1 & 2: Upload Form */}
      {showUploadForm && (
        <>
          {/* Step 1: ID Inputs */}
          <IdInputsRow />

          {/* Step 2: File Uploads */}
          <Text fontSize="xl" color="muted.400" mt={10} mb={2}>
            Upload paired-end FASTQ files (Read 1 and Read 2) from your local storage or cloud
          </Text>

          <Flex
            direction={{ base: 'column', lg: 'row' }}
            gap={6}
            w="100%"
            maxW="1440px"
            mx="auto"
            justify="center"
          >
            <Box flex="1" maxW={{ base: '100%', lg: '700px' }}>
              <FileUploadSection readNumber={1} />
            </Box>
            <Box flex="1" maxW={{ base: '100%', lg: '700px' }}>
              <FileUploadSection readNumber={2} />
            </Box>
          </Flex>

          {/* hg38 reference genome note */}
          <Box
            mt={6}
            p={4}
            bg="blue.50"
            border="1px solid"
            borderColor="blue.200"
            borderRadius="lg"
            maxW="700px"
            mx="auto"
          >
            <Flex align="center" gap={3}>
              <InfoIcon color="blue.500" boxSize={5} />
              <Text fontSize="sm" color="blue.700" fontWeight="medium" textAlign="left">
                This analysis pipeline uses the <strong>hg38 (GRCh38)</strong> reference genome. 
                Please ensure your FASTQ files are compatible with this reference.
              </Text>
            </Flex>
          </Box>

          {/* Analyze Button */}
          <Button
            mt={10}
            mb="140px"
            bg="primary.500"
            fontSize="2xl"
            color="white"
            fontWeight="bold"
            px={12}
            py={7}
            borderRadius="full"
            onClick={handleAnalysis}
            isDisabled={!idsValid || !hasBothReads}
            title={!hasBothReads ? 'Both Read 1 and Read 2 (paired-end) FASTQ files are required' : ''}
            _hover={{ bg: 'secondary.500', transform: 'scale(1.03)' }}
            _active={{ bg: 'primary.500', transform: 'scale(0.99)' }}
            transition="all 0.18s ease-in-out"
          >
            Analyze
          </Button>
        </>
      )}

      {/* Progress View */}
      {showProgress && (
        <Box textAlign="center" p={8}>
          <Text
            as="h3"
            fontSize="3xl"
            mt={10}
            mx="auto"
            maxW={{ base: '280px', md: '640px' }}
            whiteSpace="normal"
            overflowWrap="break-word"
            lineHeight="1.3"
            color="primary.500"
          >
            Mapping out insights – sit tight while we prepare your results.
          </Text>

          <Text
            mt={4}
            color="muted.500"
            mx="auto"
            maxW={{ base: '280px', md: '500px' }}
            fontSize="md"
            fontStyle="italic"
          >
            This might take a while. We'll email you when your results are ready – feel free to come back later.
          </Text>

          {!!statusDetail && (
            <Text
              mt={4}
              color="muted.400"
              mx="auto"
              maxW={{ base: '260px', md: '600px' }}
              whiteSpace="normal"
              overflowWrap="break-word"
              fontSize="lg"
            >
              {statusDetail}
            </Text>
          )}

          {progress > 0 && (
            <Text mt={4} mb={2} fontSize="xl" color="primary.500" fontWeight="bold">
              {progress}%
            </Text>
          )}

          {/* Progress bar */}
          <Flex mt={6} justify="center" align="center" maxW="500px" mx="auto">
            <Box
              position="relative"
              w="100%"
              h="24px"
              bg="surface.300"
              borderRadius="full"
              overflow="hidden"
            >
              <Box
                position="absolute"
                left={0}
                top={0}
                bottom={0}
                width={`${progress}%`}
                bg="primary.500"
                borderRadius="full"
                transition="width 0.5s ease"
              />
            </Box>
          </Flex>

          {/* Cancel button */}
          {jobId && (
            <Button
              mt={8}
              colorScheme="red"
              onClick={handleCancelJob}
              isLoading={isCancelling}
              loadingText="Cancelling..."
            >
              Cancel
            </Button>
          )}
        </Box>
      )}

      {/* Results View */}
      {showResults && (
        <>
          <Text fontSize="3xl" fontWeight="bold" mt={10} color="primary.500">
            Analysis Complete
          </Text>
          <Text fontSize="lg" color="muted.400" mt={2}>
            Your germline mutation analysis is ready for download.
          </Text>

          {/* MAF Summary Graphs */}
          <Box mt={8} maxW="1200px" mx="auto">
            <Text fontSize="xl" fontWeight="bold" mb={4} color="primary.500">
              MAF Summary Analysis
            </Text>
            <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} gap={6}>
              {GRAPH_CONFIGS.map(({ key, label, filename }) => (
                <GridItem key={key}>
                  <GraphCard
                    graphUrl={resultFiles[key]}
                    label={label}
                    filename={filename}
                    isComplete={isComplete}
                    onDownload={handleDownload}
                    onImageClick={handleGraphClick}
                  />
                </GridItem>
              ))}
            </Grid>
          </Box>

          {/* Download Buttons */}
          <Box mt={10} maxW="700px" mx="auto">
            <Text fontSize="xl" fontWeight="bold" mb={6} color="primary.500">
              Download Results
            </Text>
            <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
              <GridItem>
                <Button
                  w="100%"
                  h="60px"
                  bg="primary.500"
                  color="white"
                  fontWeight="bold"
                  fontSize="lg"
                  leftIcon={<DownloadIcon />}
                  onClick={() => handleDownload(resultFiles.vcf, 'variants.vcf')}
                  _hover={{ bg: 'secondary.500' }}
                  borderRadius="xl"
                >
                  Download VCF file
                </Button>
              </GridItem>
              <GridItem>
                <Button
                  w="100%"
                  h="60px"
                  bg="primary.500"
                  color="white"
                  fontWeight="bold"
                  fontSize="lg"
                  leftIcon={<DownloadIcon />}
                  onClick={() => handleDownload(resultFiles.annotatedVcf, 'annotated_variants.vcf')}
                  _hover={{ bg: 'secondary.500' }}
                  borderRadius="xl"
                >
                  Download Annotated VCF
                </Button>
              </GridItem>
              <GridItem>
                <Button
                  w="100%"
                  h="60px"
                  bg="primary.500"
                  color="white"
                  fontWeight="bold"
                  fontSize="lg"
                  leftIcon={<DownloadIcon />}
                  onClick={() => handleDownload(resultFiles.maf, 'variants.maf')}
                  _hover={{ bg: 'secondary.500' }}
                  borderRadius="xl"
                >
                  Download MAF files
                </Button>
              </GridItem>
              <GridItem>
                <Button
                  w="100%"
                  h="60px"
                  bg="primary.500"
                  color="white"
                  fontWeight="bold"
                  fontSize="lg"
                  leftIcon={<DownloadIcon />}
                  onClick={() => handleDownload(resultFiles.bam, 'aligned.bam')}
                  _hover={{ bg: 'secondary.500' }}
                  borderRadius="xl"
                >
                  Download BAM file
                </Button>
              </GridItem>
              <GridItem>
                <Button
                  w="100%"
                  h="60px"
                  bg="primary.500"
                  color="white"
                  fontWeight="bold"
                  fontSize="lg"
                  leftIcon={<DownloadIcon />}
                  onClick={() => handlePdfDownload(resultFiles.pdf, 'ClinicalReport.pdf')}
                  _hover={{ bg: 'secondary.500' }}
                  borderRadius="xl"
                >
                  Download PDF (Clinical Report)
                </Button>
              </GridItem>
              <GridItem>
                <Button
                  w="100%"
                  h="60px"
                  bg="primary.500"
                  color="white"
                  fontWeight="bold"
                  fontSize="lg"
                  leftIcon={<ExternalLinkIcon />}
                  onClick={() => window.open(resultFiles.pdf, '_blank', 'noopener,noreferrer')}
                  _hover={{ bg: 'secondary.500' }}
                  borderRadius="xl"
                >
                  View PDF
                </Button>
              </GridItem>
            </Grid>
          </Box>

          {/* PDF Viewer */}
          <Box mt={10} maxW="900px" mx="auto">
            <Text fontSize="xl" fontWeight="bold" mb={6} color="primary.500">
              Clinical Report Preview
            </Text>
            <Box
              width="850px"
              maxW="95%"
              mx="auto"
              border="1px solid #ccc"
              borderRadius="md"
              minH="600px"
            >
              {resultFiles.pdf ? (
                <Box
                  width="100%"
                  maxH="900px"
                  bg="surface.200"
                  overflowY="auto"
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  py={8}
                  css={{
                    '&::-webkit-scrollbar': {
                      width: '8px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: '#888',
                      borderRadius: '4px',
                    },
                  }}
                >
                  <Document
                    file={resultFiles.pdf}
                    onLoadSuccess={onDocumentLoadSuccess}
                    externalLinkTarget="_blank"
                    loading={
                      <Center h="400px">
                        <Spinner size="xl" color="primary.500" thickness="4px" />
                      </Center>
                    }
                    error={
                      <Center h="400px">
                        <Text color="red.500">Failed to load PDF preview.</Text>
                      </Center>
                    }
                  >
                    {Array.from(new Array(numPages), (_, index) => (
                      <Box
                        key={`page_${index + 1}`}
                        mb={6}
                        boxShadow="0 4px 12px rgba(0,0,0,0.15)"
                      >
                        <Page
                          pageNumber={index + 1}
                          width={800}
                          renderTextLayer={false}
                          renderAnnotationLayer={true}
                        />
                      </Box>
                    ))}
                  </Document>
                </Box>
              ) : (
                <Center h="400px">
                  <VStack>
                    <Text color="muted.400" fontSize="lg">PDF preview not available.</Text>
                    <Text color="muted.300" fontSize="sm">The clinical report may still be processing.</Text>
                  </VStack>
                </Center>
              )}
            </Box>
          </Box>

          {/* Start New Analysis */}
          <Box mt={10} textAlign="center" mb="140px">
            <Button
              variant="outline"
              borderColor="primary.500"
              color="primary.500"
              fontWeight="bold"
              px={8}
              py={6}
              borderRadius="full"
              onClick={resetToStart}
              _hover={{ bg: 'surface.100' }}
            >
              Start New Analysis
            </Button>
          </Box>
        </>
      )}

      {/* Graph Enlarge Modal */}
      <Modal isOpen={isGraphModalOpen} onClose={onGraphModalClose} size="6xl" isCentered>
        <ModalOverlay bg="blackAlpha.700" />
        <ModalContent maxW="90vw" maxH="90vh" bg="white" borderRadius="xl">
          <ModalHeader color="primary.600" borderBottom="1px solid" borderColor="surface.200">
            {selectedGraph?.label}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody p={4} display="flex" alignItems="center" justifyContent="center" overflow="auto">
            {selectedGraph && (
              <Image
                src={selectedGraph.url}
                alt={selectedGraph.label}
                maxW="100%"
                maxH="75vh"
                objectFit="contain"
              />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Previous Files Modal */}
      <Modal isOpen={isPreviousFilesOpen} onClose={onClosePreviousFiles} size="xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent maxW="700px">
          <ModalHeader>
            <HStack justify="space-between">
              <Text>Select from Previous Uploads</Text>
              <Button
                size="sm"
                variant="ghost"
                leftIcon={<RepeatIcon />}
                onClick={fetchPreviousFiles}
                isLoading={isLoadingPreviousFiles}
              >
                Refresh
              </Button>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {isLoadingPreviousFiles ? (
              <Center py={10}>
                <Spinner size="xl" color="primary.500" />
              </Center>
            ) : previousFiles.length === 0 ? (
              <Center py={10}>
                <VStack>
                  <Icon as={FiFolder} boxSize={12} color="muted.300" />
                  <Text color="muted.400" fontSize="lg">No previous files found</Text>
                  <Text color="muted.300" fontSize="sm">Upload FASTQ files from other pipelines to reuse them here</Text>
                </VStack>
              </Center>
            ) : (
              <VStack spacing={2} align="stretch">
                {/* Group by pipeline folder */}
                {Object.entries(
                  previousFiles.reduce((acc, file) => {
                    const folder = file.pipeline_folder;
                    if (!acc[folder]) acc[folder] = [];
                    acc[folder].push(file);
                    return acc;
                  }, {} as Record<string, PreviousFile[]>)
                ).map(([folder, files]) => (
                  <Box key={folder}>
                    <Text fontSize="sm" fontWeight="bold" color="muted.500" textTransform="uppercase" mb={2}>
                      {folder.replace(/_/g, ' ')}
                    </Text>
                    <VStack spacing={1} align="stretch">
                      {files.map((file) => (
                        <Flex
                          key={file.key}
                          p={3}
                          bg="surface.50"
                          borderRadius="md"
                          border="1px solid"
                          borderColor="surface.200"
                          cursor="pointer"
                          _hover={{ bg: 'surface.100', borderColor: 'primary.300' }}
                          onClick={() => selectPreviousFile(file)}
                          align="center"
                          justify="space-between"
                        >
                          <HStack spacing={3} flex={1} minW={0}>
                            <Icon as={FiFolder} color="primary.400" />
                            <Text fontWeight="medium" color="text.primary" isTruncated maxW="100%">
                              {file.filename}
                            </Text>
                          </HStack>
                          <HStack spacing={2}>
                            <Badge colorScheme="green" fontSize="xs">
                              FASTQ
                            </Badge>
                            <Text fontSize="xs" color="muted.400" whiteSpace="nowrap">
                              {formatFileSize(file.size)}
                            </Text>
                          </HStack>
                        </Flex>
                      ))}
                    </VStack>
                    <Divider my={3} />
                  </Box>
                ))}
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Feedback Modal */}
      {jobId && (
        <FeedbackModal
          isOpen={showFeedbackModal}
          onClose={() => setShowFeedbackModal(false)}
          jobId={jobId}
          pipelineType="Haplotype Caller"
          onSubmitSuccess={() => setShowFeedbackModal(false)}
        />
      )}

      {/* Chatbot */}
      <Box flexShrink={0}>
        <Chatbot />
      </Box>
    </Box>
  );
};

export default HaplotypeCaller;
